/**
 * TalentNotesSection — reusable notes thread body (composer + comment list).
 *
 * Shared between the Talent detail page and the candidate meta-CV so both
 * surfaces render (and stay visually in sync with) the same talent-level notes.
 * Presentational only: comments are sorted most-recent-first internally; the
 * caller owns the outer heading/card chrome and the add-comment wiring.
 * All copy via the i18next talentPool namespace. No hardcoded strings.
 */

import React, { useMemo } from 'react';
import { CommentComposer } from './CommentComposer';
import { CommentItem } from './CommentItem';
import type { TalentComment } from '@/types/talent.types';

export interface TalentNotesSectionProps {
  comments: TalentComment[];
  onAddComment: (body: string) => void;
  isPending?: boolean;
}

export const TalentNotesSection: React.FC<TalentNotesSectionProps> = ({
  comments,
  onAddComment,
  isPending = false,
}) => {
  const sortedComments = useMemo(
    () =>
      [...comments].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [comments],
  );

  return (
    <>
      {/* Composer on top, then the most-recent-first comment thread (known-site
          pattern). Comments are separated from each other by hairlines; plain
          whitespace separates the composer from the thread. */}
      <CommentComposer onSubmit={onAddComment} isPending={isPending} />
      {sortedComments.length > 0 && (
        <div className="mt-4 divide-y divide-light-lila/40">
          {sortedComments.map((comment) => (
            <CommentItem key={comment.id} comment={comment} />
          ))}
        </div>
      )}
    </>
  );
};

TalentNotesSection.displayName = 'TalentNotesSection';
